import api from "./api-client";
import browser from "./browser-client";
import crypto from "./crypto-library";
import datastore from "./datastore";
import folderColor from "./folder-color";
import share from "./share";
import { getStore } from "./store";
import {
  change,
  createProject,
  findRoot,
  JOURNAL,
  validateName,
  protectedMove,
  ownedProject,
} from "./project-operation";

const configuration = () => browser.getConfig("code10_projects");
export async function targetsProjectRoot(parent) {
  if (TARGET !== "webclient" || !parent?.id) return false;
  const config = await configuration();
  return config?.enabled === true && parent.id === config.parent_folder_id;
}

function session() {
  const initial = getStore().getState();
  if (!initial.user.token || !initial.user.userId)
    throw new Error("PROJECT_SESSION_REQUIRED");
  const current = () => {
    const state = getStore().getState();
    if (
      state.user.token !== initial.user.token ||
      state.user.userId !== initial.user.userId ||
      state.server.url !== initial.server.url
    )
      throw new Error("PROJECT_SESSION_CHANGED");
  };
  const request = async (method, path, payload = null) => {
    current();
    try {
      const result = await api.projectRequest(
        method,
        path,
        payload,
        initial.user.token,
        initial.user.sessionSecretKey
      );
      current();
      if (!result?.data) throw new Error("PROJECT_RESPONSE_INVALID");
      return result.data;
    } catch (error) {
      // Expose only a protocol error code, never the payload/key material.
      const code = error?.data?.code || error?.data?.data?.code;
      const failure = new Error(
        typeof code === "string" ? code : "PROJECT_REQUEST_FAILED"
      );
      failure.code = code;
      throw failure;
    }
  };
  return { current, request };
}

function prepare(root, name, color) {
  const shareKey = crypto.generateSecretKey();
  const content = crypto.encryptData(
    JSON.stringify({ name, folders: [], items: [] }),
    shareKey
  );
  const key = crypto.encryptSecretKey(shareKey);
  const roles = {};
  for (const role of ["Read", "Write", "Admin"]) {
    const secret = crypto.generateSecretKey();
    const pair = crypto.generatePublicPrivateKeypair();
    const secretEnvelope = crypto.encryptSecretKey(secret);
    const privateEnvelope = crypto.encryptSecretKey(pair.private_key);
    const grantKey = crypto.encryptData(shareKey, secret);
    const title = crypto.encryptData(name, secret);
    const type = crypto.encryptData("folder", secret);
    roles[role] = {
      public_key: pair.public_key,
      secret_key: secretEnvelope.text,
      secret_key_nonce: secretEnvelope.nonce,
      private_key: privateEnvelope.text,
      private_key_nonce: privateEnvelope.nonce,
      grant: {
        key: grantKey.text,
        key_nonce: grantKey.nonce,
        title: title.text,
        title_nonce: title.nonce,
        type: type.text,
        type_nonce: type.nonce,
      },
    };
  }
  return {
    color: folderColor.normalizeFolderColor(color),
    shareKey,
    payload: {
      root_id: root.id,
      parent_id: root.folder_id,
      project_id: crypto.generateUuid(),
      operation_id: crypto.generateUuid(),
      folder_id: crypto.generateUuid(),
      name,
      data: content.text,
      data_nonce: content.nonce,
      key: key.text,
      key_nonce: key.nonce,
      roles,
    },
  };
}

async function adapterFor(datastoreId) {
  const config = await configuration();
  const { current, request } = session();
  const response = await request("GET", "roots/");
  if (response.contract !== 1) throw new Error("PROJECT_CONTRACT_UNSUPPORTED");
  const root = response.roots.find(
    (item) =>
      item.datastore_id === datastoreId &&
      item.folder_id === config?.parent_folder_id
  );
  if (!root) throw new Error("PROJECT_ROOT_DENIED");
  const read = async () => {
    current();
    const tree = await datastore.getDatastoreWithId(datastoreId, () => {
      current();
      return true;
    });
    current();
    if (!tree?.write_date) throw new Error("PROJECT_VAULT_READ_FAILED");
    return tree;
  };
  const write = async (tree) => {
    current();
    const content = await datastore.encryptDatastore(datastoreId, tree);
    current();
    if (!content?.text) throw new Error("PROJECT_VAULT_ENCRYPT_FAILED");
    return request("PUT", `roots/${root.id}/vault/`, {
      old_write_date: tree.write_date,
      data: content.text,
      data_nonce: content.nonce,
    });
  };
  return {
    root,
    adapter: {
      current,
      request,
      prepare,
      read,
      write,
      provision: (payload) => request("POST", "provision/", payload),
      verify: (id) => request("GET", `${id}/`),
    },
  };
}

export async function newProject(parent, tree, name, color) {
  if (!(await targetsProjectRoot(parent)))
    throw new Error("PROJECT_PARENT_INVALID");
  const { root, adapter } = await adapterFor(tree.datastore_id);
  return createProject(adapter, root, name, color);
}

export async function finishProjects(tree) {
  const { root, adapter } = await adapterFor(tree.datastore_id);
  const raw = await adapter.read();
  for (const [id, operation] of Object.entries(
    raw[JOURNAL]?.operations || {}
  )) {
    if (operation.state === "complete") continue;
    await createProject(
      adapter,
      root,
      operation.payload.name,
      operation.color,
      id
    );
  }
  for (const id of Object.keys(raw[JOURNAL]?.renames || {}))
    await finishRename(adapter, root, id);
}

export const hasPendingProjects = (tree) =>
  Object.values(tree?.[JOURNAL]?.operations || {}).some(
    (op) => op.state !== "complete"
  ) || Object.keys(tree?.[JOURNAL]?.renames || {}).length > 0;

export async function registeredProject(node, tree) {
  const config = await configuration();
  if (!config?.enabled || !node?.share_id) return false;
  const { request } = session();
  return ownedProject(
    await request("GET", "roots/"),
    node,
    tree.datastore_id,
    config.parent_folder_id
  );
}

async function finishRename(adapter, root, folderId) {
  const tree = await adapter.read();
  const intent = tree[JOURNAL]?.renames?.[folderId];
  if (!intent) throw new Error("PROJECT_OPERATION_NOT_FOUND");
  const result = await adapter.request(
    "PATCH",
    `${intent.project_id}/`,
    intent.payload
  );
  const confirmed = await adapter.verify(intent.project_id);
  adapter.current();
  if (
    confirmed.revision !== result.revision ||
    confirmed.name !== intent.payload.name
  )
    throw new Error("PROJECT_COMMIT_UNVERIFIED");
  await change(adapter, (raw) => {
    const folder = findRoot(raw, root.folder_id)?.folders?.find(
      (item) => item.id === folderId
    );
    if (folder?.share_id !== confirmed.share_id)
      throw new Error("PROJECT_FOLDER_CONFLICT");
    folder.name = confirmed.name;
    folder.color = intent.color;
    raw[JOURNAL].projects[folderId] = {
      project_id: confirmed.project_id,
      revision: confirmed.revision,
    };
    delete raw[JOURNAL].renames[folderId];
    // Completed create records are retained only while their label still identifies this project.
    for (const [id, op] of Object.entries(raw[JOURNAL].operations)) {
      if (op.state === "complete" && op.payload.folder_id === folderId)
        delete raw[JOURNAL].operations[id];
    }
    return { write: true, value: confirmed };
  });
  const saved = await adapter.read();
  adapter.current();
  if (
    findRoot(saved, root.folder_id)?.folders?.find(
      (item) => item.id === folderId
    )?.name !== confirmed.name
  ) {
    throw new Error("PROJECT_COMMIT_UNVERIFIED");
  }
  return confirmed;
}

export async function renameProject(node, tree) {
  const name = validateName(node.name);
  const { root, adapter } = await adapterFor(tree.datastore_id);
  const project = root.projects.find(
    (item) => item.folder_id === node.id && item.share_id === node.share_id
  );
  if (!project) throw new Error("PROJECT_ROOT_DENIED");
  const fresh = await share.readShare(node.share_id, node.share_secret_key);
  adapter.current();
  if (!fresh?.data?.write_date) throw new Error("PROJECT_SHARE_READ_FAILED");
  const writeDate = fresh.data.write_date;
  delete fresh.data.write_date;
  fresh.data.name = name;
  fresh.data.color = folderColor.normalizeFolderColor(node.color);
  const envelope = crypto.encryptData(
    JSON.stringify(fresh.data),
    node.share_secret_key
  );
  const intent = {
    project_id: project.project_id,
    color: fresh.data.color,
    payload: {
      operation_id: crypto.generateUuid(),
      revision: project.revision,
      name,
      old_share_write_date: writeDate,
      data: envelope.text,
      data_nonce: envelope.nonce,
    },
  };
  await change(adapter, (raw) => {
    raw[JOURNAL] ||= { operations: {}, projects: {} };
    raw[JOURNAL].renames ||= {};
    if (raw[JOURNAL].renames[node.id])
      throw new Error("PROJECT_RENAME_PENDING");
    raw[JOURNAL].renames[node.id] = intent;
    return { write: true, value: intent };
  });
  return finishRename(adapter, root, node.id);
}

export async function validateMovePolicy(tree, sourcePath, targetPath, type) {
  const config = await configuration();
  if (!config?.enabled || type !== "folders") return;
  // A direct project or an ancestor containing the configured root needs an explicit policy.
  // Ordinary moves do not query an owner-only registry or affect non-owner workflows.
  const [parent, key] = datastore.findInDatastore(sourcePath, tree);
  if (
    protectedMove(
      parent[key],
      sourcePath,
      targetPath,
      type,
      config.parent_folder_id
    )
  ) {
    throw new Error("PROJECT_MOVE_REQUIRES_POLICY");
  }
}
